<template>
	<div>
		<div class="gh-layout">
			<aside class="gh-intro">
				<h2 class="gh-intro__title">Roles &amp; Permissions</h2>
				<p class="gh-intro__desc">Create and manage user roles and permissions.</p>
			</aside>

			<section class="gh-accounts">
				<div class="ada-card">
				<table v-if="!loading" class="roles-table">
					<thead>
						<tr>
							<th>Name</th>
							<th>Permissions</th>
							<th v-if="authUser.permissions.includes('ROLE_UPDATE')"></th>
						</tr>
					</thead>
					<tbody>
						<template v-for="(role, i) in roles">
							<tr :key="i">
								<td class="roles-table__name">{{ role.name }}</td>
								<td>
									<template v-for="(permission, i) in role.permissions">
										<span :title="permission.description" class="perm-chip" :key="i">
											{{ permission.name }}
										</span>
									</template>
								</td>
								<td class="text-end">
									<button
										v-if="authUser.permissions.includes('ROLE_UPDATE')"
										class="gh-icon-btn"
										title="Edit role"
										data-bs-toggle="modal"
										data-bs-target="#roleModal"
										@click="editRole(role)"
									>
										<font-awesome-icon :icon="['fas', 'pen']" />
									</button>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
				<div v-else class="text-center">
					<div class="spinner-border" role="status">
						<span class="visually-hidden">Loading...</span>
					</div>
				</div>
				<div v-if="authUser.permissions.includes('ROLE_CREATE')" class="gh-save-row mt-3">
					<button
						class="gh-save-btn"
						data-bs-toggle="modal"
						data-bs-target="#roleModal"
						@click="createRole()"
					>
						<font-awesome-icon :icon="['fas', 'plus']" />
						Create Role
					</button>
				</div>
				</div>
			</section>
		</div>
		<!-- Modal -->
		<div
			class="modal fade"
			id="roleModal"
			data-bs-backdrop="static"
			data-bs-keyboard="false"
			tabindex="-1"
			aria-labelledby="roleModalLabel"
			aria-hidden="true"
		>
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<form @submit.prevent="type === 'edit' ? updateRole() : storeRole()">
						<div class="modal-header">
							<h5 class="modal-title" id="roleModalLabel">
								{{ type === 'edit' ? 'Edit Role' : 'Create Role' }}
							</h5>
							<button
								type="button"
								class="btn-close"
								data-bs-dismiss="modal"
								aria-label="Close"
								@click="reset()"
							></button>
						</div>
						<div class="modal-body">
							<div class="form-group mb-2">
								<label for="roleName">Name</label>
								<input
									type="text"
									class="form-control"
									v-model="role.name"
									placeholder="Enter role name"
									required
								/>
							</div>

							<div class="form-group mb-2">
								<label for="">Description</label>
								<textarea class="form-control" v-model="role.description" name="" id="" rows="3">
								</textarea>
							</div>

							<label for="">Select Permissions</label>
							<multiselect
								:multiple="true"
								v-model="selectedPermissions"
								:options="optionsPermissions"
								required
							>
							</multiselect>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" @click="reset()" data-bs-dismiss="modal">
								Close
							</button>
							<button v-if="type === 'edit'" type="submit" class="btn btn-primary">
								Update
							</button>
							<button v-if="type === 'new'" type="submit" class="btn btn-primary">
								Save
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapState } from 'vuex'
import restApi from '../../api'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
export default {
	name: 'Roles',
	components: { Multiselect },
	data() {
		return {
			error: '',
			roles: [],
			role: {},
			type: '',
			selectedPermissions: null,
			optionsPermissions: [],
			$roleModal: null,
			loading: false,
			existingPlace: null,
		}
	},
	computed: {
		...mapState(['authUser']),
	},
	created() {
		this.loadRoles()
		this.loadPermissions()
	},
	mounted() {
		this.$roleModal = new Modal('#roleModal')
	},
	methods: {
		initMarker(loc) {
			this.existingPlace = loc
		},
		loadRoles() {
			this.loading = true
			restApi
				.get('/roles')
				.then(({ data }) => {
					this.roles = data
					this.loading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},
		editRole(role) {
			this.role = role
			this.type = 'edit'
			let selectedPermissions = []
			role.permissions.forEach(permission => {
				selectedPermissions.push(permission.name)
			})
			this.selectedPermissions = selectedPermissions
		},
		createRole() {
			this.type = 'new'
		},
		updateRole() {
			let permissions = this.selectedPermissions.toString()
			restApi
				.put('/role/' + this.role.id, { ...this.role, perms: permissions })
				.then(() => {
					this.loadRoles()
					this.$roleModal.hide()
					this.$swal.fire({
						icon: 'success',
						title: 'Role successfully updated',
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'roleStore'),
						timer: 6000,
					})
				})
		},
		storeRole() {
			let permissions = this.selectedPermissions.toString()
			restApi
				.post('/role', { ...this.role, perms: permissions })
				.then(() => {
					this.loadRoles()
					this.$roleModal.hide()
					this.$swal.fire({
						icon: 'success',
						title: 'Role successfully added',
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'roleStore'),
						timer: 6000,
					})
				})
		},
		reset() {
			this.role = {}
			this.type = ''
			this.selectedPermissions = null
		},
		loadPermissions() {
			// Get all permisions from API
			restApi
				.get('/permissions')
				.then(({ data }) => {
					this.optionsPermissions = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
	},
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
